import React, { useState, useRef, useCallback, useEffect } from 'react';
import useGridStitcher from './hooks/useGridStitcher';
import debounce from './utils/debounce';
import gridLayouts from './config/gridLayouts';

import ToolSelector from './components/ToolSelector';
import UploadTool from './components/UploadTool';
import ScaleTool from './components/ScaleTool';
import CropTool from './components/CropTool';
import FormatTool from './components/FormatTool';
import GridTool from './components/GridTool';
import ImagePreview from './components/ImagePreview';
import Tooltip from './components/Tooltip';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher';

const ImageProcessor = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language || 'zh';
    document.title = t('app.title');
  }, [i18n.language, t]);
  // 宫格拼接器
  const { stitch } = useGridStitcher();

  // 状态管理
  const [selectedImage, setSelectedImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [activeTool, setActiveTool] = useState('upload');
  const [zoom, setZoom] = useState(100);
  const [cropSettings, setCropSettings] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(0.95);
  const [gridSettings, setGridSettings] = useState({
    layout: '2x2',
    customRows: 2,
    customCols: 2,
    images: [],
    fitMode: 'cover',
    spacing: 2,
    canvasSize: 800
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showTooltip, setShowTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });

  // Refs
  const canvasRef = useRef(null);
  const imagePreviewRef = useRef(null);
  const fileInputRef = useRef(null);
  const gridFileInputRef = useRef(null);

  // 工具切换
  const handleToolChange = (tool) => {
    if (tool === 'upload') {
      setSelectedImage(null);
      setProcessedImage(null);
    } else if (tool === 'grid') {
      if (!processedImage || activeTool !== 'grid') {
        setProcessedImage(null);
      }
    } else if (['scale', 'crop', 'format'].includes(tool) && selectedImage) {
      setProcessedImage(selectedImage);
      if (tool === 'scale') setZoom(100);
      if (tool === 'crop') setCropSettings({ x: 0, y: 0, width: 100, height: 100 });
    }
    setActiveTool(tool);
  };

  // 上传
  const handleImageUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target.result);
        setProcessedImage(event.target.result);
        handleToolChange('scale');
      };
      reader.readAsDataURL(file);
    }
  };

  // 缩放
  const handleZoom = (percentage) => {
    const newZoom = Math.max(10, Math.min(200, percentage));
    setZoom(newZoom);
    processImage('scale');
  };

  // 裁剪预览（防抖）
  const debouncedProcessCrop = useCallback(
    debounce((settings) => {
      if (!selectedImage) return;
      const img = new Image();
      img.src = selectedImage;
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const cropWidth = img.width * (settings.width / 100);
        const cropHeight = img.height * (settings.height / 100);
        const cropX = img.width * (settings.x / 100);
        const cropY = img.height * (settings.y / 100);
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        let dataUrl;
        if (format === 'jpeg' || format === 'webp') {
          dataUrl = canvas.toDataURL(`image/${format}`, quality);
        } else {
          dataUrl = canvas.toDataURL(`image/${format}`);
        }
        setProcessedImage(dataUrl);
      };
    }, 100),
    [selectedImage, format, quality]
  );

  const handleCropValueChange = (field, value) => {
    let newValue = Number(value);
    if (isNaN(newValue)) return;
    switch (field) {
      case 'width':
      case 'height':
        newValue = Math.max(10, Math.min(100, newValue));
        break;
      case 'x':
        newValue = Math.max(0, Math.min(100 - cropSettings.width, newValue));
        break;
      case 'y':
        newValue = Math.max(0, Math.min(100 - cropSettings.height, newValue));
        break;
      default:
        break;
    }
    const newSettings = { ...cropSettings, [field]: newValue };
    setCropSettings(newSettings);
    debouncedProcessCrop(newSettings);
  };

  const applyCrop = () => {
    processImage('crop');
  };

  // 格式
  const handleFormatChange = (newFormat) => {
    setFormat(newFormat);
    processImage('format');
  };

  // 宫格图片添加
  const addImageToGrid = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        const layout = gridLayouts.find((l) => l.id === gridSettings.layout);
        const maxImages = layout.areas.length;
        if (file.type.startsWith('image/') && gridSettings.images.length < maxImages) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
              setGridSettings((prev) => {
                const newImages = [
                  ...prev.images,
                  { src: event.target.result, width: img.width, height: img.height, aspectRatio: img.width / img.height }
                ];
                return { ...prev, images: newImages };
              });
            };
          };
          reader.readAsDataURL(file);
        }
      });
      e.target.value = '';
    }
  };

  // 宫格布局与设置
  const changeGridLayout = (layoutId) => {
    const layout = gridLayouts.find((l) => l.id === layoutId);
    if (layout) {
      setGridSettings((prev) => ({
        ...prev,
        layout: layoutId,
        customRows: layout.rows,
        customCols: layout.cols,
        images: prev.images.slice(0, layout.areas.length)
      }));
    }
  };

  const changeGridDimensions = (rows, cols) => {
    const newRows = Math.max(1, Math.min(5, rows));
    const newCols = Math.max(1, Math.min(5, cols));
    setGridSettings((prev) => ({
      ...prev,
      layout: `${newRows}x${newCols}`,
      customRows: newRows,
      customCols: newCols,
      images: prev.images.slice(0, newRows * newCols)
    }));
  };

  const removeGridImage = (index) => {
    setGridSettings((prev) => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return { ...prev, images: newImages };
    });
  };

  const updateGridSetting = (key, value) => {
    setGridSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Tooltip
  const showToolTip = (text, e) => {
    setShowTooltip({ visible: true, text, x: e.clientX + 10, y: e.clientY + 10 });
    setTimeout(() => {
      setShowTooltip((prev) => ({ ...prev, visible: false }));
    }, 2000);
  };

  // 处理图片
  const processImage = (tool) => {
    if (!selectedImage && tool !== 'grid') return;
    setIsProcessing(true);

    // 宫格生成
    if (tool === 'grid') {
      const { images, layout, canvasSize, spacing, fitMode } = gridSettings;
      const layoutConfig = gridLayouts.find((l) => l.id === layout);

      if (images.length === 0) {
        setIsProcessing(false);
        return;
      }

      const imageSources = images.map((img) => img.src);

      stitch({
        images: imageSources,
        rows: layoutConfig?.rows || gridSettings.customRows,
        cols: layoutConfig?.cols || gridSettings.customCols,
        areas: layoutConfig?.isHeterogeneous ? layoutConfig.areas : undefined,
        totalSize: canvasSize,
        spacing,
        fitMode,
        format,
        quality
      })
        .then((dataUrl) => {
          setProcessedImage(dataUrl);
          setIsProcessing(false);
        })
        .catch(() => {
          setIsProcessing(false);
        });

      return;
    }

    // 其他工具
    const img = new Image();
    img.src = selectedImage;
    img.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      switch (tool) {
        case 'scale': {
          const scaledWidth = img.width * (zoom / 100);
          const scaledHeight = img.height * (zoom / 100);
          canvas.width = scaledWidth;
          canvas.height = scaledHeight;
          ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);
          break;
        }
        case 'crop': {
          const cropWidth = img.width * (cropSettings.width / 100);
          const cropHeight = img.height * (cropSettings.height / 100);
          const cropX = img.width * (cropSettings.x / 100);
          const cropY = img.height * (cropSettings.y / 100);
          canvas.width = cropWidth;
          canvas.height = cropHeight;
          ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
          break;
        }
        case 'format': {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          break;
        }
        default:
          break;
      }

      let dataUrl;
      if (format === 'jpeg' || format === 'webp') {
        dataUrl = canvas.toDataURL(`image/${format}`, quality);
      } else {
        dataUrl = canvas.toDataURL(`image/${format}`);
      }
      setProcessedImage(dataUrl);
      setIsProcessing(false);
    };
  };

  // 下载
  const downloadImage = () => {
    if (!processedImage) return;
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = `processed-image.${format}`;
    link.click();
    showToolTip('图片已下载', { clientX: window.innerWidth - 100, clientY: 50 });
  };

  // 清空
  const clearImage = () => {
    setSelectedImage(null);
    setProcessedImage(null);
    setActiveTool('upload');
    setZoom(100);
    setCropSettings({ x: 0, y: 0, width: 100, height: 100 });
    setFormat('png');
    setQuality(0.95);
    setGridSettings({
      layout: '2x2',
      customRows: 2,
      customCols: 2,
      images: [],
      fitMode: 'cover',
      spacing: 2,
      canvasSize: 800
    });
  };

  // 渲染当前工具面板
  const renderActiveToolPanel = () => {
    switch (activeTool) {
      case 'upload':
        return <UploadTool fileInputRef={fileInputRef} handleImageUpload={handleImageUpload} />;
      case 'scale':
        return (
          <ScaleTool
            zoom={zoom}
            handleZoom={handleZoom}
            processImage={processImage}
            isProcessing={isProcessing}
            showToolTip={showToolTip}
          />
        );
      case 'crop':
        return (
          <CropTool
            cropSettings={cropSettings}
            handleCropValueChange={handleCropValueChange}
            applyCrop={applyCrop}
            isProcessing={isProcessing}
            showToolTip={showToolTip}
          />
        );
      case 'format':
        return (
          <FormatTool
            format={format}
            quality={quality}
            setQuality={setQuality}
            handleFormatChange={handleFormatChange}
            processImage={processImage}
            isProcessing={isProcessing}
            showToolTip={showToolTip}
          />
        );
      case 'grid':
        return (
          <GridTool
            gridLayouts={gridLayouts}
            gridSettings={gridSettings}
            changeGridLayout={changeGridLayout}
            changeGridDimensions={changeGridDimensions}
            updateGridSetting={updateGridSetting}
            addImageToGrid={addImageToGrid}
            removeGridImage={removeGridImage}
            processImage={processImage}
            isProcessing={isProcessing}
            showToolTip={showToolTip}
            gridFileInputRef={gridFileInputRef}
            quality={quality}
            setQuality={setQuality}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <header className="bg-white shadow-sm py-6 px-4 sticky top-0 z-10">
        <div className="container mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 text-center">{t('header.title')}</h1>
          <p className="text-gray-500 text-center mt-2">{t('header.subtitle')}</p>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="container mx-auto py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <ToolSelector
            activeTool={activeTool}
            selectedImage={selectedImage}
            handleToolChange={handleToolChange}
            showToolTip={showToolTip}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ImagePreview
                activeTool={activeTool}
                processedImage={processedImage}
                selectedImage={selectedImage}
                isProcessing={isProcessing}
                gridSettings={gridSettings}
                clearImage={clearImage}
                downloadImage={downloadImage}
                refEl={imagePreviewRef}
              />
            </div>

            <div>{renderActiveToolPanel()}</div>
          </div>
        </div>
      </main>

      <footer className="bg-white mt-12 py-6 px-4 border-t">
        <div className="container mx-auto text-center text-gray-500 text-sm">
          <p>{t('app.title')} &copy; {new Date().getFullYear()} - {t('app.copyright')}</p>
        </div>
      </footer>

      <canvas ref={canvasRef} className="hidden"></canvas>

      <Tooltip showTooltip={showTooltip} />
    </div>
  );
};

export default ImageProcessor;